import {
    Smartphone, Download, Check, Star, Shield, Heart, Users,
    Home, MapPin, Calendar, MessageCircle, Mail, Phone, Globe, Lock,
    Sparkles, Zap, Gift, Rocket, BookOpen, Camera, Music, Coffee
} from 'lucide-react';

/**
 * L'apparence d'un bloc : son icône, sa couleur, son alignement.
 *
 * ========================================================================
 * UN NOM EN BASE, UN COMPOSANT ICI
 * ========================================================================
 *
 * La base stocke « smartphone », jamais du SVG. Stocker du balisage
 * reviendrait à stocker du code que personne ne relit, et qu'aucun filtre ne
 * saurait vérifier — alors qu'un nom inconnu se réduit ici à « pas d'icône ».
 *
 * Le serveur valide déjà ces noms contre sa propre liste (VALEURS_ENUM dans
 * cabaza_pages.cfc). Cette table en est le miroir : un nom ajouté là-bas et
 * oublié ici n'affiche rien, ce qui se voit — l'inverse serait refusé à
 * l'enregistrement, ce qui se voit aussi.
 *
 * ========================================================================
 * POURQUOI DES CLASSES FIGÉES ET NON UNE COULEUR LIBRE
 * ========================================================================
 *
 * Tailwind compile les classes qu'il TROUVE DANS LE CODE : `bg-${couleur}-100`
 * construit à l'exécution ne produirait aucune règle CSS, et la pastille
 * serait transparente. D'où des classes écrites en toutes lettres.
 *
 * C'est aussi ce qui tient la charte : l'éditeur choisit parmi huit couleurs
 * qui vont ensemble, au lieu de saisir un code hexadécimal qui jure.
 */

const ICONES = {
    smartphone: Smartphone,
    download: Download,
    check: Check,
    star: Star,
    shield: Shield,
    heart: Heart,
    users: Users,
    home: Home,
    mappin: MapPin,
    calendar: Calendar,
    message: MessageCircle,
    mail: Mail,
    phone: Phone,
    globe: Globe,
    lock: Lock,
    sparkles: Sparkles,
    zap: Zap,
    gift: Gift,
    rocket: Rocket,
    book: BookOpen,
    camera: Camera,
    music: Music,
    coffee: Coffee
};

/**
 * Le composant d'icône, ou null si aucune n'est choisie.
 *
 * La casse est ignorée : la base stocke en minuscules, mais un contenu
 * importé pourrait porter « mapPin ».
 */
export const iconeDe = (nom) => {
    if (!nom || nom === 'aucune') return null;
    return ICONES[String(nom).toLowerCase()] || null;
};

/**
 * Les classes d'une pastille colorée : fond clair, icône soutenue.
 *
 * `cabaza` est le dégradé de la charte, sur fond plein et icône blanche —
 * c'est la variante mise en avant, celle du bloc principal d'une page.
 */
const COULEURS = {
    cabaza: { pastille: 'bg-gradient-to-br from-cabaza-purple to-cabaza-pink', icone: 'text-white' },
    violet: { pastille: 'bg-purple-100', icone: 'text-purple-600' },
    rose:   { pastille: 'bg-pink-100',   icone: 'text-pink-600' },
    bleu:   { pastille: 'bg-blue-100',   icone: 'text-blue-600' },
    vert:   { pastille: 'bg-green-100',  icone: 'text-green-600' },
    ambre:  { pastille: 'bg-amber-100',  icone: 'text-amber-600' },
    rouge:  { pastille: 'bg-red-100',    icone: 'text-red-600' },
    gris:   { pastille: 'bg-gray-100',   icone: 'text-gray-500' }
};

export const couleurDe = (nom) => COULEURS[String(nom || 'cabaza').toLowerCase()] || COULEURS.cabaza;

/** Les classes d'alignement d'un bloc. */
export const alignementDe = (nom) => (
    String(nom).toLowerCase() === 'centre'
        ? { conteneur: 'text-center', pastille: 'mx-auto', onglets: 'justify-center' }
        : { conteneur: '', pastille: '', onglets: '' }
);

/**
 * Le nombre de colonnes d'une grille.
 *
 * `auto` s'adapte au nombre de cartes : trois cartes tiennent en trois
 * colonnes, quatre en quatre. C'est ce qu'on attend sans y penser, et c'est
 * pourquoi c'est le défaut.
 */
export const colonnesDe = (nom, nbElements) => {
    const choix = String(nom || 'auto').toLowerCase();

    if (choix === '2') return 'sm:grid-cols-2';
    if (choix === '3') return 'sm:grid-cols-2 lg:grid-cols-3';
    if (choix === '4') return 'sm:grid-cols-2 lg:grid-cols-4';

    if (nbElements >= 4) return 'sm:grid-cols-2 lg:grid-cols-4';
    if (nbElements === 3) return 'sm:grid-cols-2 lg:grid-cols-3';
    return 'sm:grid-cols-2';
};

/**
 * La puce d'une étape : son icône si elle en a une, son numéro sinon.
 *
 * LE NUMÉRO RESTE LE DÉFAUT. Une suite d'étapes se lit dans l'ordre, et le
 * rang est l'information la plus utile — l'icône ne vient que l'illustrer.
 * Elle remplace le numéro plutôt que de s'y ajouter : deux marqueurs côte à
 * côte encombrent la colonne sans rien dire de plus.
 *
 * Même pastille dégradée dans les deux cas : la colonne reste alignée qu'une
 * étape porte une icône ou non.
 */
export const Puce = ({ index, icone }) => {
    const Icone = iconeDe(icone);

    return (
        <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white bg-gradient-to-br from-cabaza-purple to-cabaza-pink">
            {Icone ? <Icone className="w-4 h-4" /> : index + 1}
        </span>
    );
};
